import type { VariableDef, VariablesSchema } from '@emailforge/schemas';
import { Button, Input, Select, Textarea } from '@emailforge/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import { useGetTemplateQuery, useSaveVersionMutation } from '@/features/templates/templatesApi';
import { t } from '@/lib/i18n';

export function VariablesScreen() {
  const { templateId = '' } = useParams();
  const navigate = useNavigate();
  const { data } = useGetTemplateQuery(templateId, { skip: !templateId });
  const [saveVersion, { isLoading: isSaving }] = useSaveVersionMutation();

  const [fields, setFields] = useState<VariableDef[]>([]);
  const [sampleData, setSampleData] = useState<string>('{}');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.latestVersion) return;
    setFields(data.latestVersion.variablesSchema.fields);
    setSampleData(JSON.stringify(data.latestVersion.variablesSchema.sampleData ?? {}, null, 2));
  }, [data?.latestVersion?.id, data?.latestVersion]);

  const addField = () =>
    setFields([
      ...fields,
      { key: `var_${fields.length + 1}`, type: 'string', required: false },
    ]);

  const updateField = (i: number, patch: Partial<VariableDef>) => {
    const next = [...fields];
    next[i] = { ...next[i]!, ...patch };
    setFields(next);
  };

  const removeField = (i: number) => setFields(fields.filter((_, j) => j !== i));

  const save = async () => {
    setError(null);
    if (!data?.latestVersion) return;
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(sampleData);
    } catch (err) {
      setError('Sample data is not valid JSON: ' + (err as Error).message);
      return;
    }
    const next: VariablesSchema = { version: 1, fields, sampleData: parsed };
    try {
      await saveVersion({
        templateId,
        astJson: data.latestVersion.astJson,
        variablesSchema: next,
        changeSummary: 'Updated variables schema',
      }).unwrap();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <>
      <TopBar
        crumbs={[t('nav.templates'), data?.name ?? '…', 'Variables']}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/app/templates/${templateId}`)}>
              Back to editor
            </Button>
            <Button variant="flame" size="sm" onClick={save} disabled={isSaving || !data?.latestVersion}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, padding: 24, background: 'var(--paper-2)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16 }}>
          <div className="mf-card">
            <div className="mf-card-head">
              <span className="mf-card-title">Schema fields</span>
              <div style={{ flex: 1 }} />
              <Button size="sm" onClick={addField}>
                <Icon.plus size={12} /> Add field
              </Button>
            </div>

            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {fields.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)', fontSize: 13 }}>
                  No variables yet. Click <b>Add field</b> to define the payload shape for{' '}
                  <code className="mono">POST /api/send/:id</code>.
                </div>
              )}
              {fields.map((f, i) => (
                <div
                  key={i}
                  className="mf-card"
                  style={{
                    padding: 12,
                    display: 'grid',
                    gridTemplateColumns: '1.2fr 0.8fr 0.5fr auto',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <Input mono
                    
                    placeholder="variable_key"
                    value={f.key}
                    onChange={(e) => updateField(i, { key: e.target.value })} />
                  <Select
                    
                    value={f.type ?? 'string'}
                    onChange={(e) => updateField(i, { type: e.target.value as VariableDef['type'] })}>
                    <option value="string">string</option>
                    <option value="number">number</option>
                    <option value="boolean">boolean</option>
                    <option value="date">date</option>
                    <option value="url">url</option>
                    <option value="email">email</option>
                  </Select>
                  <label style={{ fontSize: 12, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={!!f.required}
                      onChange={(e) => updateField(i, { required: e.target.checked })}
                    />
                    required
                  </label>
                  <Button variant="ghost" size="sm" icon onClick={() => removeField(i)}>
                    <Icon.trash size={12} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="mf-card">
            <div className="mf-card-head">
              <span className="mf-card-title">Sample data</span>
              <div style={{ flex: 1 }} />
              <span
                className="mono"
                style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
              >
                JSON
              </span>
            </div>
            <Textarea mono
              
              value={sampleData}
              onChange={(e) => setSampleData(e.target.value)}
              spellCheck={false}
              style={{
                height: 360,
                padding: 14,
                borderRadius: 0,
                border: 'none',
                fontSize: 12,
                fontFamily: 'var(--font-mono)',
                lineHeight: 1.6,
                resize: 'vertical',
                background: 'var(--code-bg)',
                color: 'var(--code-ink)',
              }} />
            {error && (
              <div
                style={{
                  background: 'var(--err-soft)',
                  color: 'var(--err)',
                  padding: '10px 14px',
                  fontSize: 12.5,
                  borderTop: '1px solid oklch(0.85 0.10 25)',
                }}
              >
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
